import React from "react";
import { AlertTriangle, CheckCircle2, Activity, Droplet, Wine, ShieldPlus } from "lucide-react";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import SectionHeading from "../components/SectionHeading";
import AppointmentCTA from "../components/AppointmentCTA";
import FAQSection from "../components/FAQSection";
import { images, cirrhosisFaqs, clinic } from "../data/mock";

const causes = [
  { icon: Droplet, t: "Chronic Fatty Liver / NASH", d: "Long-standing fat and inflammation leading to progressive scarring." },
  { icon: Wine, t: "Long-term Alcohol Use", d: "Sustained alcohol consumption is a leading cause of liver scarring." },
  { icon: ShieldPlus, t: "Chronic Hepatitis B & C", d: "Untreated viral hepatitis gradually damages liver tissue over years." },
  { icon: Activity, t: "Other Causes", d: "Autoimmune conditions, bile duct disease and certain metabolic disorders." },
];

const symptoms = ["Persistent fatigue & weakness", "Loss of appetite & nausea", "Yellowing of eyes & skin (jaundice)", "Swelling in legs & abdomen (ascites)", "Easy bruising or bleeding", "Confusion or memory issues (advanced)"];

const stages = [
  { title: "Compensated Cirrhosis", desc: "The liver is scarred but still functioning. Often no obvious symptoms. Early treatment can preserve function and prevent progression." },
  { title: "Decompensated Cirrhosis", desc: "Liver function is significantly reduced with complications such as jaundice, fluid buildup and bleeding. Requires intensive, specialist management." },
];

const Cirrhosis = () => {
  return (
    <div>
      <SEO path="/liver-cirrhosis-treatment-varanasi" faqs={cirrhosisFaqs} />
      <PageHero breadcrumb="Liver Cirrhosis Treatment"
        title="Liver Cirrhosis Treatment in Varanasi"
        subtitle="Expert diagnosis, management and long-term care for liver cirrhosis and chronic liver disease." />

      {/* WHAT IS */}
      <section className="py-16 sm:py-20">
        <div className="container-app grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading center={false} eyebrow="Overview" title="What is Liver Cirrhosis?" />
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>Liver cirrhosis is advanced, permanent scarring (fibrosis) of the liver caused by long-term, ongoing damage. Each time the liver is injured, it tries to repair itself and forms scar tissue. As more scar tissue builds up, it replaces healthy tissue and slowly blocks blood flow through the liver, reducing its ability to filter toxins, produce proteins and support digestion.</p>
              <p>Cirrhosis usually develops silently over many years due to conditions such as fatty liver disease (NASH), chronic hepatitis B or C, and long-term alcohol use. In its early "compensated" stage there may be no symptoms at all — the liver keeps functioning despite the damage — which is why many people are unaware until it is advanced.</p>
              <p>While existing scarring cannot be reversed, <strong className="text-slate-800">early diagnosis and proper treatment can stop or slow its progression</strong>, control complications and protect the healthy liver tissue that remains. With timely, specialist care from {clinic.doctorName}, many patients with early cirrhosis live healthy, active lives for years.</p>
            </div>
          </div>
          <div>
            <img src={images.liverStages} alt="Stages of liver damage leading to cirrhosis" className="w-full rounded-3xl shadow-xl border border-slate-100 bg-white" />
          </div>
        </div>
      </section>

      {/* CAUSES */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <SectionHeading eyebrow="Causes" title="What Causes Liver Cirrhosis?" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {causes.map((c, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-sky-500 text-white grid place-items-center mb-5"><c.icon size={24} /></div>
                <h3 className="font-heading font-semibold text-slate-900 mb-2">{c.t}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SYMPTOMS + STAGES */}
      <section className="py-16 sm:py-20">
        <div className="container-app grid lg:grid-cols-2 gap-12">
          <div>
            <SectionHeading center={false} eyebrow="Warning Signs" title="Symptoms of Cirrhosis" />
            <div className="space-y-3">
              {symptoms.map((s, i) => (
                <div key={i} className="flex items-center gap-3 bg-sky-50/60 border border-sky-100 rounded-xl px-5 py-4">
                  <AlertTriangle size={20} className="text-sky-500 shrink-0" />
                  <span className="text-sm font-medium text-slate-700">{s}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <SectionHeading center={false} eyebrow="Stages" title="Stages of Cirrhosis" />
            <div className="space-y-4">
              {stages.map((s, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                  <h3 className="font-heading font-semibold text-lg text-sky-600 mb-2">{s.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DIAGNOSIS */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <SectionHeading eyebrow="Diagnosis" title="How Cirrhosis is Diagnosed"
            subtitle="A combination of tests confirms the diagnosis, identifies the cause and measures severity." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { t: "FibroScan / Elastography", d: "Measures liver stiffness to assess the degree of fibrosis and scarring non-invasively." },
              { t: "Liver Function Tests", d: "Blood tests (bilirubin, albumin, INR, SGPT/SGOT) that reveal how well the liver is working." },
              { t: "Ultrasound / CT / MRI", d: "Imaging to examine liver texture, size, blood flow and detect nodules or fluid." },
              { t: "Upper GI Endoscopy", d: "Checks for varices (swollen veins) in the food pipe that can cause dangerous bleeding." },
              { t: "Viral & Autoimmune Panel", d: "Identifies the underlying cause such as hepatitis B/C or autoimmune conditions." },
              { t: "Liver Biopsy", d: "Occasionally used to confirm the stage and cause when other tests are inconclusive." },
            ].map((c, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm">
                <span className="font-heading text-sky-500 font-bold text-xl">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-heading font-semibold text-slate-900 mt-2 mb-2">{c.t}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TREATMENT */}
      <section className="py-16 sm:py-20">
        <div className="container-app">
          <SectionHeading eyebrow="Treatment & Management" title="How Cirrhosis is Managed"
            subtitle="The goal is to treat the underlying cause, prevent complications and preserve liver function." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { t: "Treat the Root Cause", d: "Antiviral therapy for hepatitis, alcohol cessation support, and fatty liver management." },
              { t: "Prevent Complications", d: "Regular monitoring and endoscopy to detect and manage varices, ascites and infections early." },
              { t: "Diet & Nutrition", d: "Customized low-salt, balanced nutrition plans to support liver health and overall strength." },
              { t: "Medication Management", d: "Carefully selected medicines to control symptoms and protect remaining liver function." },
              { t: "Lifestyle Guidance", d: "Complete guidance on alcohol, activity and habits that impact liver recovery." },
              { t: "Regular Follow-up", d: "Ongoing FibroScan and lab monitoring to track progression and adjust care." },
            ].map((c, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-lg transition-all">
                <CheckCircle2 size={26} className="text-sky-500 mb-4" />
                <h3 className="font-heading font-semibold text-slate-900 mb-2">{c.t}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLICATIONS */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <div className="bg-white border border-red-100 rounded-3xl p-8 sm:p-12 max-w-4xl mx-auto shadow-sm">
            <SectionHeading center={false} eyebrow="Complications" title="Possible Complications of Cirrhosis" />
            <p className="text-slate-600 leading-relaxed mb-5">As cirrhosis advances, reduced liver function can lead to serious complications that require prompt specialist care:</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                "Ascites — fluid buildup in the abdomen",
                "Variceal bleeding — bleeding from swollen veins",
                "Jaundice — yellowing of skin and eyes",
                "Hepatic encephalopathy — confusion from toxin buildup",
                "Increased infection risk",
                "Higher risk of liver cancer (HCC)",
              ].map((c, i) => (
                <div key={i} className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                  <AlertTriangle size={18} className="text-red-500 mt-0.5 shrink-0" />
                  <span className="text-sm font-medium text-slate-700">{c}</span>
                </div>
              ))}
            </div>
            <p className="text-slate-600 leading-relaxed mt-5">Regular monitoring and early intervention can prevent or effectively manage most of these complications.</p>
          </div>
        </div>
      </section>

      {/* WHY EARLY */}
      <section className="py-16 sm:py-20">
        <div className="container-app">
          <div className="bg-sky-50 rounded-3xl p-8 sm:p-12 border border-sky-100 max-w-4xl mx-auto text-center">
            <div className="w-14 h-14 rounded-2xl bg-sky-500 text-white grid place-items-center mx-auto mb-6"><ShieldPlus size={28} /></div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Why Early Treatment Matters</h2>
            <p className="text-slate-600 leading-relaxed text-lg">
              Cirrhosis is progressive, but early intervention can dramatically change outcomes. Detecting liver scarring in its compensated stage allows treatment that can halt progression, prevent life-threatening complications and protect your quality of life. The earlier you consult, the more options you have.
            </p>
          </div>
        </div>
      </section>

      <FAQSection faqs={cirrhosisFaqs} title="Liver Cirrhosis — Frequently Asked Questions" />
      <AppointmentCTA title="Get Expert Cirrhosis Care — Don't Wait" />
    </div>
  );
};

export default Cirrhosis;
